import React, { useEffect, useRef } from 'react'
import { MedChangeLog } from '../../../../types/models/MedChangeLog'
import { useModal } from '../../../../shared'
import { MedChangeModalBody } from '../../components/MedChangeModalBody'

/**
 * 薬剤変更モーダルフック（TanStack Query版）
 *
 * MedChangeModalBody を別コンポーネントに抽出し、key prop パターンで
 * medChangeLog が変わるたびにフォーム状態をリセットする。
 *
 * refreshMedLog パラメータは不要。
 * Mutation の onSuccess で invalidateQueries により自動更新される。
 *
 * @param medChangeLog - 編集対象の MedChangeLog。undefined の場合は新規作成モード
 *
 * 使用例:
 * ```tsx
 * const { openModal, renderModal } = useMedChangeModal(selectedLog)
 * // ...
 * {renderModal()}
 * ```
 */
export const useMedChangeModal = (
  medChangeLog?: MedChangeLog
): {
  renderModal: () => React.ReactNode
  openModal: () => void
} => {
  // useModal から closeModal を取得し、MedChangeModalBody に渡すための ref
  // useModal が定義される前に MedChangeModalBody で closeModal を参照する必要があるため、
  // ref を介して遅延バインディングを行う
  const closeModalRef = useRef<() => void>(() => {})

  // key prop: medChangeLog が変わるたびにコンポーネントを再マウントし、
  // 全ての state を初期値にリセットする
  const modalContent = (
    <MedChangeModalBody
      key={medChangeLog?.id ?? 'new'}
      medChangeLog={medChangeLog}
      onClose={() => closeModalRef.current()}
    />
  )

  const { openModal, closeModal, renderModal } = useModal(
    modalContent,
    'center'
  )

  // closeModal を ref に保存（useEffect 内で行い、レンダー中の ref 更新を避ける）
  useEffect(() => {
    closeModalRef.current = closeModal
  }, [closeModal])

  return {
    renderModal,
    openModal,
  }
}
